import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross1Icon,
  InfoCircledIcon,
} from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { isAxiosError } from "axios";
import {
  ArrowDownAzIcon,
  DownloadIcon,
  PuzzleIcon,
  RefreshCwIcon,
} from "lucide-react";
import {
  Fragment,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { mergeProps, useFocusWithin } from "react-aria";
import { flushSync } from "react-dom";
import {
  T,
  RecordProps,
  Editor,
  TLPointerEventInfo,
  useEditor,
  TLShapeUtilCanBindOpts,
  TLShape,
} from "tldraw";
import { useSharedResizeObserver } from "use-shared-resize-observer";
import { useSize } from "use-shared-resize-observer/use-size";

import {
  buildBindings,
  expandParameterLevels,
  parameterLevelsForShape,
  replaceCustomNodeParameters,
} from "@/components/canvas-editor/build-manifest";
import {
  CanvasEditorMachine,
  setDebug,
} from "@/components/canvas-editor/canvas-editor-machine";
import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  BlockHeader,
  BlockSeparator,
  BlockTitle,
  ZoomLabelContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasButton,
  CanvasInput,
  CanvasNumberInput,
  CanvasPercentageInput,
  CanvasSimpleSelect,
  CanvasSwitch,
} from "@/components/canvas-editor/components/Inputs";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  CustomNodeProps,
  CustomNodeShape,
  customNodeType,
  Dimensions,
  LayoutBinding,
  metadataType,
  pinnableShapes,
} from "@/components/canvas-editor/shapes/shapes";
import {
  preventBubble,
  ToolStateNode,
  useIsReadonlyEditor,
} from "@/components/canvas-editor/utils";
import { CanvasRegexTextArea } from "@/components/CanvasRegexTextArea";
import { CustomNodeDiff } from "@/components/CustomNodeDiffEditor";
import { ListWrapper, ReadOnlyValue } from "@/components/hooks/ListContainer";
import { useEvent } from "@/components/hooks/useEvent";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Blockquote, Link } from "@/components/ui/typography";
import {
  CustomNodeHistoryItem,
  getCustomNodeHistoryQueryOptions,
  getCustomNodeQueryOptions,
} from "@/lib/api";
import {
  FeedInputParameter,
  FeedListParameter,
  FeedNumberParameter,
  FeedParameter,
  FeedSelectParameter,
  FeedToggleParameter,
  FilterTypes,
  groupCustomNodeParameters,
} from "@/lib/types";
import { cn } from "@/lib/utils";

export type CustomToolInfo = TLPointerEventInfo &
  Pick<CustomNodeShape["props"], "filter" | "node">;

export class CustomNodeTool extends ToolStateNode {
  static override id = customNodeType;

  info = {} as CustomToolInfo;

  static override uiToolDef = (editor: Editor) => ({
    id: customNodeType,
    icon: PuzzleIcon,
    label: CustomNodeShapeUtil.docs.title,
    defaultColor: CustomNodeShapeUtil.docs.defaultColor,
    onSelect: () => {
      editor.setCurrentTool(customNodeType);
    },
    onActivate: (node: Pick<CustomNodeShape["props"], "filter" | "node">) => {
      editor.setCurrentTool(customNodeType, node);
    },
    docs: () => (
      <NodeDocs
        editor={editor}
        component={CustomNode}
        util={CustomNodeShapeUtil}
      />
    ),
  });

  override onEnter(info: CustomToolInfo) {
    this.info = info;
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: customNodeType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
      props: {
        filter: this.info.filter,
        node: this.info.node,
        metadata: this.info.filter.metadata,
      },
    });

    this.editor.setCurrentTool("select");
  }
}

export class CustomNodeShapeUtil extends BindableUtil<CustomNodeShape> {
  static override migrations = createMigrations(customNodeType);
  static override type = customNodeType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Custom Node",
    description:
      "Allows you to only accept/reject content matches your filter level for a given prediction. English Only",
  };

  override getType() {
    return customNodeType;
  }

  static override props: RecordProps<CustomNodeShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    h: T.number,
    filter: T.any,
    isReadOnly: T.boolean.optional(),
    node: T.object({
      title: T.string,
      description: T.string.optional(),
      component_id: T.number,
      version_id: T.number,
      // The dependency constraint this embed declares. Optional because every
      // shape created before this existed has none, and absence means `pinned`
      // everywhere -- client, wire, and server.
      dependency_intent: T.literalEnum("pinned", "compatible", "latest").optional(),
    }),
    padding: T.object({
      top: T.number,
      left: T.number,
      right: T.number,
      bottom: T.number,
      gap: T.number,
    }),
    showLogic: T.boolean.optional(),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 500,
      h: 60,
      filter: {} as FilterTypes,
      isReadOnly: false,
      node: {
        title: "Custom Node",
        component_id: 0,
        version_id: 0,
      },
      padding: {
        top: 82,
        bottom: 16,
        right: 16,
        left: 16,
        gap: 14,
      },
      showLogic: false,
    };
  }

  override canBind({
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<CustomNodeShape>) {
    if (bindingType === "arrow") {
      return true;
    }

    return pinnableShapes.includes(toShapeType) && bindingType === "layout";
  }

  override component(shape: CustomNodeShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <CustomNode
        shape={shape}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
        updateShape={this.updateProps.bind(this)}
      />
    );
  }
}

function ListParamater({
  parameter,
  parameterIndex,
  shape,
  updateShape,
}: {
  parameter: FeedListParameter;
  shape: CustomNodeShape;
  parameterIndex: number;
  updateShape: (
    shape: CustomNodeShape,
    props: Partial<CustomNodeProps>,
  ) => void;
}) {
  const inputListRef = useRef<HTMLDivElement>(null);
  const values = parameter.value ?? parameter.exampleValue;
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: setIsFocusWithin,
  });
  const onAddAnother = useCallback(() => {
    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
    flushSync(() => {
      updateShape(shape, {
        filter: {
          ...shape.props.filter,
          metadata: {
            ...shape.props.filter.metadata,
            customNodeParameters:
              shape.props.filter.metadata?.customNodeParameters?.map((p, i) =>
                i === parameterIndex
                  ? ({
                      ...p,
                      value: [...values, ""],
                    } as FeedListParameter)
                  : p,
              ),
          },
        },
      });
    });

    if (!inputListRef.current) {
      return;
    }

    const inputs =
      inputListRef.current.querySelectorAll<HTMLInputElement>("input");

    inputs.values().toArray().at(-1)?.focus();
  }, [parameterIndex, shape, updateShape, values]);

  return (
    <div className="flex flex-col gap-2" ref={inputListRef}>
      <ListWrapper
        {...focusWithinProps}
        onKeyDownCapture={(e) => {
          if (e.key === "Enter" && e.metaKey) {
            e.preventDefault();
            e.stopPropagation();
            onAddAnother();
          }
        }}
      >
        {values.map((value, index) => (
          <div
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`param-value-${shape.id}-${index}`}
            className="relative group"
          >
            <CanvasInput
              className="flex-1"
              value={value}
              data-list-input={true}
              onValueChange={(v) => {
                updateShape(shape, {
                  filter: {
                    ...shape.props.filter,
                    metadata: {
                      ...shape.props.filter.metadata,
                      customNodeParameters:
                        shape.props.filter.metadata?.customNodeParameters?.map(
                          (param, i) =>
                            i === parameterIndex
                              ? ({
                                  ...param,
                                  value: values.map((_, i2) =>
                                    i2 === index ? v : _,
                                  ),
                                } as FeedListParameter)
                              : param,
                        ),
                    },
                  },
                });
              }}
            />
            <CanvasButton
              className="pointer-events-auto absolute right-1 top-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
              size="smIcon"
              variant="ghost"
              onPress={() => {
                updateShape(shape, {
                  filter: {
                    ...shape.props.filter,
                    metadata: {
                      ...shape.props.filter.metadata,
                      ...shape.props.metadata,
                      customNodeParameters:
                        shape.props.filter.metadata?.customNodeParameters?.map(
                          (p, i) =>
                            i === parameterIndex
                              ? ({
                                  ...p,
                                  value: values.filter((_, i2) => i2 !== index),
                                } as FeedListParameter)
                              : p,
                        ) || [],
                    },
                  },
                });
              }}
            >
              <Cross1Icon />
            </CanvasButton>
          </div>
        ))}
      </ListWrapper>
      <div className="flex items-center gap-2">
        <CanvasButton variant="ghost" className="flex-1" onPress={onAddAnother}>
          Add Another
          {isFocusWithin && (
            <KbdGroup className="hidden md:flex absolute top-1/2 right-2 -translate-y-1/2">
              <Kbd>Meta + ⏎</Kbd>
            </KbdGroup>
          )}
        </CanvasButton>
        <SimpleTooltip message="Sort Alphabetically">
          <CanvasButton
            variant="ghost"
            size="icon"
            onClick={() => {
              const value = parameter.value ?? parameter.exampleValue;
              const sorted = [...value].sort();

              if (JSON.stringify(sorted) === JSON.stringify(value)) {
                sorted.reverse();
              }

              updateShape(shape, {
                filter: {
                  ...shape.props.filter,
                  metadata: {
                    ...shape.props.filter.metadata,
                    customNodeParameters:
                      shape.props.filter.metadata?.customNodeParameters?.map(
                        (p, i) =>
                          i === parameterIndex
                            ? ({
                                ...p,
                                // `value` is this user's choice, `exampleValue`
                                // is the author's default. Sorting is a choice,
                                // so it belongs in `value`: writing
                                // exampleValue edited the default instead, and
                                // did nothing at all once `value` was set,
                                // because substitution reads
                                // `value ?? exampleValue`.
                                value: sorted,
                              } as FeedListParameter)
                            : p,
                      ),
                  },
                },
              });
            }}
          >
            <span className="sr-only">Sort Alphabetically</span>
            <ArrowDownAzIcon />
          </CanvasButton>
        </SimpleTooltip>
      </div>
    </div>
  );
}

function PreviewResizeObserver({
  areaRef,
  updateShape,
  shape,
}: {
  areaRef: React.RefObject<HTMLDivElement>;
  updateShape: (
    shape: CustomNodeShape,
    props: Partial<CustomNodeProps>,
  ) => void;
  shape: CustomNodeShape;
}) {
  const showZoom = use(ZoomLabelContext);

  useSharedResizeObserver({
    ref: areaRef,
    onUpdate: (entry) => {
      const blockSize = entry.borderBoxSize[0]?.blockSize;

      if (!blockSize || !showZoom || !shape.props.showLogic) return;

      updateShape(shape, {
        h: blockSize + 40,
        padding: {
          ...shape.props.padding,
          top: blockSize + 40,
        },
      });
    },
  });

  return null;
}

function UpdateDialog({
  shape,
  latestNode,
  setUpdateDialogOpen,
}: {
  shape: CustomNodeShape;
  latestNode: CustomNodeHistoryItem;
  setUpdateDialogOpen: (open: boolean) => void;
}) {
  const { data: history } = useQuery(
    getCustomNodeHistoryQueryOptions(latestNode.id),
  );
  const [resetParameters, setResetParameters] = useState(false);
  const editor = useEditor();
  const onManualUpdate = useEvent(() => {
    const [parentBinding] = editor.getBindingsToShape<LayoutBinding>(
      shape.id,
      "layout",
    );

    if (!parentBinding) return;

    const parentShape = editor.getShape(parentBinding.fromId);

    if (!parentShape) return;
    if (!latestNode) return;

    editor.deleteShape(shape.id);

    // Same merge the automatic reconcile uses, so an explicit update and a
    // server-driven one can never mean different things.
    const merged = mergeNodeVersion({ shape, latestNode, resetParameters });

    requestAnimationFrame(() => {
      editor.createShape({
        id: shape.id,
        type: customNodeType,
        x: shape.x,
        y: shape.y,
        props: merged,
      });

      editor.createBinding({
        toId: shape.id,
        fromId: parentShape.id,
        type: "layout",
        props: { index: parentBinding.props.index, placeholder: false },
      });

      setUpdateDialogOpen(false);
    });
  });
  const changelog = useMemo(() => {
    const fullHistory = [latestNode, ...(history || [])];
    const currentVersion = fullHistory.findIndex(
      (h) => h.version_id === shape.props.node.version_id,
    );

    if (!currentVersion) return [];
    if (currentVersion === -1) return [];

    return fullHistory.slice(0, currentVersion);
  }, [history, latestNode, shape.props.node]);

  return (
    <AlertDialog open={true} onOpenChange={setUpdateDialogOpen}>
      <AlertDialogContent
        {...preventBubble}
        className="pointer-events-auto"
        variant="fullscreen"
      >
        <div className="flex flex-col gap-4 h-full">
          <AlertDialogHeader>
            <AlertDialogTitle>Update Custom Node</AlertDialogTitle>
            <AlertDialogDescription>
              Review the changes to the custom node and apply the update.
            </AlertDialogDescription>
            {latestNode.version_message && (
              <Blockquote className="flex flex-col gap-2">
                {changelog.map(
                  (item) =>
                    item.version_message && (
                      <div className="flex gap-1" key={item.version_id}>
                        <p className="text-sm font-medium text-slate-dim pt-[2px]">
                          {item.version_id}:
                        </p>
                        <MarkdownRenderer>
                          {item.version_message}
                        </MarkdownRenderer>
                      </div>
                    ),
                )}
              </Blockquote>
            )}
          </AlertDialogHeader>
          <Separator />
          <CustomNodeDiff shape={shape} latestNode={latestNode} />
          <AlertDialogFooter>
            <div className="flex-1 py-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="reset-parameters"
                  checked={resetParameters}
                  onCheckedChange={(checked) =>
                    setResetParameters(Boolean(checked))
                  }
                />
                <label
                  htmlFor="add-to-top"
                  className="text-sm font-medium leading-none"
                >
                  Reset Parameters to Default
                </label>
              </div>
            </div>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onManualUpdate}>
              Apply Update
            </AlertDialogAction>
          </AlertDialogFooter>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Would moving between these two parameter sets lose or change what the feed
 * relies on?
 *
 * A rename or a retype means the values stored on this node may no longer mean
 * anything, so the move needs a person to agree to it. Added parameters are
 * compatible -- they take the author's default -- so only what the installed
 * version already relies on is compared.
 *
 * Shared by the "Apply Update" path and the version picker, so that the two can
 * never disagree about which moves are safe. Moving BACKWARDS is judged the
 * same way and by the same rule: the direction does not matter, only whether
 * the parameters the node depends on survive.
 */
function moveIsBreaking(
  installed: FeedParameter[] | undefined,
  incoming: FeedParameter[] | undefined,
) {
  if (!installed || !incoming) return false;

  const signature = (parameters: FeedParameter[]) =>
    parameters
      .map((parameter) => `${parameter.name}:${parameter.type ?? "input"}`)
      .sort()
      .join("|");

  const incomingByName = new Map(incoming.map((p) => [p.name, p]));
  const survivors = installed.filter((p) => incomingByName.has(p.name));
  if (survivors.length !== installed.length) return true;

  return (
    signature(survivors) !==
    signature(survivors.map((p) => incomingByName.get(p.name)!))
  );
}

/**
 * Merge a newer version of a node into an installed one, keeping the values the
 * user chose.
 *
 * Shared by the explicit "Apply Update" button and the automatic reconcile
 * below, so the two can never disagree about what applying an update means.
 * Parameters are matched by name — the only identity a parameter has — and a
 * customized title survives.
 */
function mergeNodeVersion({
  shape,
  latestNode,
  resetParameters = false,
}: {
  shape: CustomNodeShape;
  latestNode: CustomNodeHistoryItem;
  resetParameters?: boolean;
}) {
  const metadata = {
    ...shape.props.filter.metadata,
    ...latestNode.component.metadata,
    // The auto-update choice belongs to the INSTALL, not to the component, and
    // it lives on `shape.props.metadata` -- neither spread above carries it.
    // Without this, applying a version silently cleared the flag, which made
    // auto-update DISABLE ITSELF: the reconcile effect below runs only while
    // trackLatest is true, and the merge it runs is what turned it off. The
    // next save then sent track_latest:false and the feed stopped tracking.
    customNodeAutoUpdate: shape.props.metadata?.customNodeAutoUpdate,
    title:
      shape.props.metadata?.title !== shape.props.metadata?.component_name
        ? shape.props.metadata?.title
        : latestNode.component.metadata?.title,
    customNodeParameters:
      latestNode.component.metadata?.customNodeParameters?.map((param) => ({
        ...param,
        value: resetParameters
          ? param.exampleValue
          : shape.props.filter.metadata?.customNodeParameters?.find(
              (p) => p.name === param.name,
            )?.value ?? param.value,
      })),
  };

  return {
    filter: { ...latestNode.component, metadata },
    node: {
      title: latestNode.title,
      description: latestNode.description,
      component_id: latestNode.id,
      version_id: latestNode.version_id,
    },
    metadata,
  } as Partial<CustomNodeProps>;
}

/**
 * Choose which version of a custom node this feed runs.
 *
 * Until now the only version a node could be moved to was the newest one, via
 * "Apply Update" or auto-update. An owner who wanted to stay on a version that
 * works, or to go back to one after an update changed the results, had nothing
 * to click -- and nothing told them other versions existed. Measured on prod
 * when this was written: 1,019 installs were running a version behind their
 * component, and 998 of them had auto-update off, so nothing was ever going to
 * move them.
 *
 * Picking a version PINS it, which means turning auto-update off. That is not a
 * side effect worth hiding: the reconcile effect above runs whenever
 * `trackLatest` is true and drags the shape back to newest, so a pin that left
 * auto-update on would be silently undone on the next render. Tracking the
 * newest version and pinning an older one are contradictory instructions, and
 * the server takes the same line -- `keep_current` turns tracking off for the
 * same reason.
 *
 * The move goes through `mergeNodeVersion`, exactly as "Apply Update" does, so
 * parameter values, a custom title and the auto-update flag are carried across
 * by one shared code path rather than two that can drift apart.
 */
function CustomNodeVersionPicker({
  shape,
  updateShape,
  latestNode,
  isReadOnly,
}: {
  shape: CustomNodeShape;
  updateShape: (shape: CustomNodeShape, props: Partial<CustomNodeProps>) => void;
  latestNode: CustomNodeHistoryItem | undefined;
  isReadOnly: boolean;
}) {
  const [open, setOpen] = useState(false);
  // Only fetch the history once the owner asks to see it: most nodes never
  // open this menu, and the list is not needed to render the node.
  const { data: history } = useQuery({
    ...getCustomNodeHistoryQueryOptions(shape.props.node.component_id),
    enabled: open && Boolean(shape.props.node.component_id),
  });

  const installedVersion = shape.props.node.version_id;
  const trackLatest = Boolean(shape.props.metadata?.customNodeAutoUpdate);

  // Newest first, and deduplicated: `latestNode` is also the first history
  // entry once the author has published more than once.
  const versions = useMemo(() => {
    const all = [...(latestNode ? [latestNode] : []), ...(history ?? [])];
    const seen = new Map<number, CustomNodeHistoryItem>();
    for (const item of all) {
      if (!seen.has(item.version_id)) seen.set(item.version_id, item);
    }
    return [...seen.values()].sort((a, b) => b.version_id - a.version_id);
  }, [history, latestNode]);

  const choose = useEvent((target: CustomNodeHistoryItem) => {
    setOpen(false);
    if (target.version_id === installedVersion && !trackLatest) return;

    const merged = mergeNodeVersion({ shape, latestNode: target });
    // Pinning means not tracking. Both copies of the metadata have to say so:
    // `mergeNodeVersion` returns the same object as `filter.metadata` and as
    // `metadata`, and the save path reads the second while the canvas renders
    // from the first, so setting only one leaves them disagreeing.
    const metadata = { ...merged.metadata, customNodeAutoUpdate: false };
    updateShape(shape, {
      ...merged,
      metadata,
      filter: { ...merged.filter, metadata },
    });
  });

  if (isReadOnly) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <SimpleTooltip message="Choose which version of this node your feed runs.">
        <MenuTrigger asChild className="pointer-events-auto" {...preventBubble}>
          <CanvasButton variant="ghost">
            Version {installedVersion}
            <ChevronDownIcon />
          </CanvasButton>
        </MenuTrigger>
      </SimpleTooltip>
      <MenuContent align="start" className="max-w-80" {...preventBubble}>
        <MenuLabel>Run this node at</MenuLabel>
        {versions.length === 0 ? (
          <MenuItem disabled>Loading versions…</MenuItem>
        ) : (
          versions.map((item) => {
            const isInstalled = item.version_id === installedVersion;
            const isLatest = item.version_id === latestNode?.version_id;
            const breaking = moveIsBreaking(
              shape.props.filter.metadata?.customNodeParameters,
              item.component.metadata?.customNodeParameters,
            );
            return (
              <MenuItem
                key={item.version_id}
                onSelect={() => choose(item)}
                className="flex flex-col items-start gap-0.5"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">Version {item.version_id}</span>
                  {isInstalled && (
                    <span className="text-xs text-slate-dim">• current</span>
                  )}
                  {isLatest && !isInstalled && (
                    <span className="text-xs text-slate-dim">• latest</span>
                  )}
                </div>
                {item.version_message && (
                  <span className="text-xs text-slate-dim line-clamp-2">
                    {item.version_message}
                  </span>
                )}
                {breaking && !isInstalled && (
                  // Say what it costs rather than blocking the move: going back
                  // to a version that works is a legitimate thing to want, and
                  // the owner is the one who knows whether these values matter.
                  <span className="text-xs text-amber-11 dark:text-amberdark-11">
                    Changes this node&apos;s settings; some of your values will
                    be reset.
                  </span>
                )}
              </MenuItem>
            );
          })
        )}
        {trackLatest && (
          <>
            <MenuSeparator />
            <MenuLabel className="font-normal text-xs text-slate-dim">
              Auto-update is on, so this node follows the newest version.
              Choosing one here turns that off.
            </MenuLabel>
          </>
        )}
      </MenuContent>
    </DropdownMenu>
  );
}

export function CustomNode({
  isBound,
  shape,
  className,
  isSelected,
  updateShape,
  ...props
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: CustomNodeShape;
  className?: string;
  updateShape: (
    shape: CustomNodeShape,
    props: Partial<CustomNodeProps>,
  ) => void;
}) {
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  // Set when the expanded preview could not be built (see updatePreview). Local
  // state, not a shape prop: it is a fact about this render, it re-tests on the
  // next mount, and it needs no schema migration.
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const {
    data: latestNode,
    isFetching,
    error: latestNodeError,
  } = shape.props.node.component_id
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery(getCustomNodeQueryOptions(shape.props.node.component_id))
    : { data: undefined, isFetching: false, error: null };

  // The node names a component the server has never heard of. Seen on prod
  // 2026-10-23: hand-built JSON embedded "Include Regex List" as component
  // 13619 (really 238) with its values already written into the body. With no
  // template to restore (see the restore effect below) and no `$PARAM` left to
  // substitute, every parameter edit landed in the metadata and never reached
  // the filter -- silently, across ~28 live feeds. Say so instead.
  const componentMissing =
    isAxiosError(latestNodeError) && latestNodeError.response?.status === 404;
  const resetParameters = useEvent(() => {
    updateShape(shape, {
      filter: {
        ...shape.props.filter,
        metadata: {
          ...shape.props.filter.metadata,
          customNodeParameters:
            latestNode?.component.metadata?.customNodeParameters?.map(
              (param) =>
                ({
                  ...param,
                  value: param.exampleValue,
                } as FeedParameter),
            ),
        },
      },
    });
  });

  const manualUpdateState = isFetching
    ? "loading"
    : latestNode?.version_id === shape.props.node.version_id
    ? "up-to-date"
    : "update-available";

  const trackLatest = Boolean(shape.props.metadata?.customNodeAutoUpdate);
  const readOnlyRender = useIsReadonlyEditor() || shape.props.isReadOnly;

  // Whether moving to the newest version would change the parameter contract.
  // Checked locally so this holds even before the server has classified the
  // move: a rename or a retype means the values stored here may no longer mean
  // anything, and applying it silently is exactly what must not happen.
  const updateIsBreaking = useMemo(() => {
    return moveIsBreaking(
      shape.props.filter.metadata?.customNodeParameters,
      latestNode?.component.metadata?.customNodeParameters,
    );
  }, [shape.props.filter.metadata?.customNodeParameters, latestNode]);

  // An auto-updating node whose newest version cannot be applied without a
  // decision. Computed entirely from THIS shape, because the shape is the only
  // thing on the canvas that knows which install it is.
  //
  // This used to also light up from the server's `pending_review` list, matched
  // on component id alone — the canvas has no feed id, so a hold on ANY of the
  // owner's feeds badged the node on ALL of them, and the tooltip then printed
  // that other feed's version number. Measured on prod 2026-10-01: 10 real
  // holds, 28 feeds badged, so 18 of the 28 badges (64%) named a feed the owner
  // was not looking at. One owner had a single hold and sixteen badges.
  //
  // Worse, it could not be cleared from here: "Apply Update" below edits this
  // canvas, it does not call `accept_update`, so the row it was complaining
  // about stayed exactly where it was no matter what the owner did — including
  // removing and re-adding the node.
  //
  // The server's list is already surfaced where it can be acted on and where
  // the feed is known: `NodeAttentionBanner` on the feed page filters
  // `/components/attention` by `algorithm_id` and offers Accept / Keep.
  const needsReview = Boolean(
    trackLatest &&
      updateIsBreaking &&
      latestNode &&
      latestNode.version_id !== shape.props.node.version_id,
  );

  // Catch the canvas up to a server-side auto-update.
  //
  // The editor rebuilds from the tldraw snapshot saved with the feed, which
  // holds this node's template as of the last manual edit. When a node tracks
  // latest, the propagation worker has already moved the FEED to the newest
  // version, so without this the canvas shows old logic for a feed running new
  // logic — and the next save would re-send the old version and undo the
  // update.
  //
  // Deliberately skipped when the move is breaking: that needs a decision from
  // the owner, and the "Needs Review" affordance below is what asks for it.
  // Read-only renders (previews, marketplace) are never touched.
  useEffect(() => {
    if (readOnlyRender || !trackLatest || !latestNode) return;
    if (latestNode.version_id === shape.props.node.version_id) return;
    if (updateIsBreaking) return;

    updateShape(shape, mergeNodeVersion({ shape, latestNode }));
  }, [
    readOnlyRender,
    trackLatest,
    latestNode,
    updateIsBreaking,
    shape,
    updateShape,
  ]);

  // Whether the reconcile effect above is about to move this shape to the
  // newest version. While that is pending there is no point restoring the
  // body at the old version first — the merge rebuilds it from the newest
  // template anyway.
  const reconcileWillRun = Boolean(
    !readOnlyRender &&
      trackLatest &&
      latestNode &&
      latestNode.version_id !== shape.props.node.version_id &&
      !updateIsBreaking,
  );

  // The template at the shape's own version, for the restore effect below.
  // Usually that is just the latest component; the history fetch only happens
  // for a shape pinned to an older version.
  const { data: nodeHistory } = useQuery({
    ...getCustomNodeHistoryQueryOptions(shape.props.node.component_id),
    enabled: Boolean(
      !readOnlyRender &&
        !reconcileWillRun &&
        shape.props.node.component_id &&
        latestNode &&
        latestNode.version_id !== shape.props.node.version_id,
    ),
  });
  const installedVersionNode = useMemo(() => {
    if (!latestNode) return undefined;
    if (latestNode.version_id === shape.props.node.version_id)
      return latestNode;
    return nodeHistory?.find(
      (item) => item.version_id === shape.props.node.version_id,
    );
  }, [latestNode, nodeHistory, shape.props.node.version_id]);

  // Restore the component's TEMPLATE into the shape when the body has lost
  // its $PARAM placeholders.
  //
  // The shape must hold the template because expansion happens at build time:
  // buildTree substitutes the current parameter values on every save and
  // preview. Two loaders hand the shape an already-EXPANDED body instead —
  // buildBindings whenever the canvas is rebuilt from a manifest (every
  // content keystroke in the JSON editor, and any canvas built from a stored
  // component body rather than a snapshot), and snapshots saved after such a
  // rebuild. A shape in that state shows parameter edits in the UI but can
  // never apply them: replaceCustomNodeParameters finds no placeholder to
  // substitute, so the saved clauses stay frozen at whatever was baked in.
  //
  // Restoring is same-version — the breaking-move guard doesn't apply, since
  // the parameter contract at one version is by definition unchanged — and it
  // reuses mergeNodeVersion so the user's parameter values, custom title and
  // auto-update choice survive exactly as they do for Apply Update. A healthy
  // shape already holds the template byte-for-byte, so this settles after at
  // most one write.
  useEffect(() => {
    if (readOnlyRender || reconcileWillRun || !installedVersionNode) return;

    const body = (filter: FilterTypes) => {
      const rest = { ...filter } as Record<string, unknown>;
      delete rest.metadata;
      return rest;
    };

    if (
      JSON.stringify(body(shape.props.filter)) ===
      JSON.stringify(body(installedVersionNode.component))
    )
      return;

    updateShape(
      shape,
      mergeNodeVersion({ shape, latestNode: installedVersionNode }),
    );
  }, [readOnlyRender, reconcileWillRun, installedVersionNode, shape, updateShape]);

  const isReadOnly = useIsReadonlyEditor() || shape.props.isReadOnly || false;
  const editor = useEditor();
  const isBoundToCustomNode = useMemo(() => {
    let currentShape: TLShape | undefined = shape;

    while (currentShape?.parentId) {
      currentShape = editor.getShape(currentShape.parentId);

      if (currentShape?.type === customNodeType) {
        return true;
      }
    }

    return false;
  }, [editor, shape]);
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  const deletePreview = useEvent(() => {
    const boundToShape = editor.getBindingsFromShape(shape.id, "layout");

    updateShape(shape, {
      h: 60,
      padding: {
        ...shape.props.padding,
        top: 82,
      },
    });
    boundToShape.forEach((binding) => {
      editor.deleteShape(binding.toId);
    });
  });

  const areaRef = useRef<HTMLDivElement>(null!);
  const size = useSize(areaRef);
  const audit = CanvasEditorMachine.useSelector((state) => {
    return state.context.debugPostUrl
      ? state.context.debugResults[state.context.debugPostUrl]
      : undefined;
  });

  const isExample = (props as { "data-example"?: boolean })["data-example"];
  const updatePreview = useEvent(() => {
    if (isExample) return;

    const boundToShape = editor.getBindingsFromShape(shape.id, "layout");

    boundToShape.forEach((binding) => {
      editor.deleteShape(binding.toId);
    });

    try {
      buildBindings({
        editor,
        toId: shape.id,
        // Expanded one parameter level at a time, innermost first -- NOT in one
        // merged pass over every ancestor's parameters, which is what made this
        // preview disagree with what publishing produces. See
        // expandParameterLevels.
        filter: expandParameterLevels(
          shape.props.filter,
          parameterLevelsForShape(editor, shape),
        ),
        isReadOnly: true,
      });
    } catch (error) {
      // The expanded preview is a read-only convenience, so a template the
      // canvas has no shape for must fail the PREVIEW rather than the node.
      // `buildBindings` throws on any leaf it does not recognise, and the loop
      // above has already deleted the old preview shapes by then -- so an
      // escaping throw left the node with no body AND in tldraw's grey error
      // state, from a single click on the expand chevron. Reported on
      // component 1417, whose body is `{"regex": [...]}`; `regex` is not one of
      // the four real regex operators and no condition in the database uses it.
      //
      // Deliberately NOT swallowed inside `buildBindings` itself: on the LOAD
      // path a throw is the honest outcome, because quietly skipping a leaf
      // there would drop it from the manifest on the next save.
      console.error("Could not expand this custom node's preview", error);
      // The rule guards against cascading renders, which this cannot cause: it
      // is a one-shot terminal transition inside a catch, and `showLogic` goes
      // false on the same line below, so the effect that called us will not run
      // this path again.
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setPreviewUnavailable(true);
      updateShape(shape, { showLogic: false });

      return;
    }

    setDebug(editor, audit);
  });

  useEffect(() => {
    if (shape.props.showLogic) {
      updatePreview();
    }
  }, [
    shape.props.showLogic,
    updatePreview,
    deletePreview,
    shape.props.filter.metadata?.customNodeParameters,
  ]);

  const onHeightChange = useEvent(() => {
    if (!shape.props.showLogic) {
      // Reset to default when collapsed - use actual measured height if available
      const collapsedHeight = size.height ? 56 + size.height : 60;
      updateShape(shape, {
        h: collapsedHeight,
        padding: {
          ...shape.props.padding,
          top: 82,
        },
      });
      return;
    }

    // When expanded, update both height and padding based on actual content
    const expandedHeight = 56 + (size.height || 0);
    updateShape(shape, {
      h: expandedHeight,
      padding: {
        ...shape.props.padding,
        top: expandedHeight,
      },
    });
  });

  useEffect(() => {
    onHeightChange();
  }, [size.height, shape.props.showLogic, isReadOnly, onHeightChange]);

  const debouncedIsCollapsed = useDebounce(shape.props.isCollapsed, 100);
  const groups = useMemo(() => {
    if (!shape.props.filter.metadata?.customNodeParameters) return [];

    return groupCustomNodeParameters(
      shape.props.filter.metadata?.customNodeParameters || [],
    );
  }, [shape.props.filter.metadata?.customNodeParameters]);

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        width={Math.max(shape.props.minW || 0, shape.props.w)}
        height={shape.props.h}
        variant={
          shape.props.metadata?.color ||
          shape.props.filter.metadata?.color ||
          CustomNodeShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={debouncedIsCollapsed}
      >
        <PreviewResizeObserver
          areaRef={areaRef}
          updateShape={updateShape}
          shape={shape}
        />
        <BlockHeader
          descriptionVariant="emphasis"
          description={
            <div className="flex flex-col gap-2">
              <div>
                {shape.props.metadata?.description ||
                  shape.props.node.description}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Link
                  to="/user/$userId"
                  params={{ userId: latestNode?.graze_user_id.toString() }}
                >
                  Built by {latestNode?.user.handle}
                </Link>
                {latestNode && (
                  <span className="text-xs text-slate-dim">
                    Version {shape.props.node.version_id}
                    {latestNode.version_id !== shape.props.node.version_id && (
                      <span className="ml-1">
                        (Latest: {latestNode.version_id})
                      </span>
                    )}
                    {shape.props.metadata?.customNodeAutoUpdate && (
                      <span className="ml-1">• Auto-update enabled</span>
                    )}
                  </span>
                )}
              </div>
            </div>
          }
        >
          {shape.props.metadata?.title ? (
            <BlockTitle
              customTitle={shape.props.metadata.title}
              title={shape.props.node.title}
            />
          ) : (
            <BlockTitle
              customTitle={shape.props.node.title}
              title="custom node"
            />
          )}

          {!shape.props.metadata?.customNodeAutoUpdate &&
            latestNode &&
            !isBoundToCustomNode &&
            manualUpdateState === "update-available" && (
              <button
                {...mergeProps(preventBubble, {
                  onClick: () => setUpdateDialogOpen(true),
                })}
                type="button"
                disabled={isReadOnly}
                className="pointer-events-auto text-xs py-1 px-2 flex items-center justify-center shrink-0 bg-red-5 dark:bg-reddark-5 rounded-md  text-red-12 dark:text-reddark-12 border border-red-8 dark:border-reddark-8"
              >
                Update Available
              </button>
            )}

          {/*
            An auto-updating node whose newest version changed its parameters
            incompatibly, so the reconcile effect above deliberately did NOT
            apply it — the stored values may no longer mean anything — and this
            node keeps running its current version until the owner decides.

            Every version named here is THIS shape's own. See `needsReview`.
          */}
          {needsReview && !isBoundToCustomNode && (
            <SimpleTooltip
              message={`Version ${latestNode?.version_id} changes this node's parameters, so it was not applied automatically. This feed is still running version ${shape.props.node.version_id}; review the change before applying it.`}
            >
              <button
                {...mergeProps(preventBubble, {
                  onClick: () => setUpdateDialogOpen(true),
                })}
                type="button"
                disabled={isReadOnly}
                className="pointer-events-auto text-xs py-1 px-2 flex items-center justify-center shrink-0 bg-amber-5 dark:bg-amberdark-5 rounded-md text-amber-12 dark:text-amberdark-12 border border-amber-8 dark:border-amberdark-8"
              >
                Needs Review
              </button>
            </SimpleTooltip>
          )}
        </BlockHeader>

        <BlockBody ref={areaRef}>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {componentMissing && !isBoundToCustomNode && (
              <div
                role="alert"
                className="text-xs p-2 rounded-md bg-red-3 dark:bg-reddark-3 text-red-12 dark:text-reddark-12 border border-red-7 dark:border-reddark-7"
              >
                <strong className="font-semibold">
                  Custom node #{shape.props.node.component_id} doesn't exist.
                </strong>{" "}
                This node's rules are fixed as they were saved, so changing its
                parameters here won't change what it matches. Replace it with
                &ldquo;{shape.props.node.title}&rdquo; from the custom node
                library.
              </div>
            )}
            {!isReadOnly && (
              <div className="flex items-center gap-2">
                <CustomNodeVersionPicker
                  shape={shape}
                  updateShape={updateShape}
                  latestNode={latestNode}
                  isReadOnly={isReadOnly}
                />
              </div>
            )}
            {shape.props.metadata?.customNodeAutoUpdate ? (
              isReadOnly ? null : (
                <SimpleTooltip message="Turn off auto-update. To update the custom node's logic, you will need to manually click the update button.">
                  <CanvasButton
                    variant="ghost"
                    className="flex-1"
                    onClick={() =>
                      updateShape(shape, {
                        metadata: {
                          ...shape.props.metadata,
                          customNodeAutoUpdate: false,
                        },
                      })
                    }
                  >
                    Disable Auto-Update
                  </CanvasButton>
                </SimpleTooltip>
              )
            ) : isReadOnly ? (
              <div className="text-sm text-slate-dim h-6 flex items-center justify-center">
                Auto-update disabled
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <SimpleTooltip
                  message={
                    manualUpdateState === "loading"
                      ? "Loading..."
                      : manualUpdateState === "up-to-date"
                      ? "No updates available."
                      : "Apply update"
                  }
                  asChild
                >
                  <div className="flex-">
                    <CanvasButton
                      variant="ghost"
                      disabled={
                        manualUpdateState === "loading" ||
                        manualUpdateState === "up-to-date"
                      }
                      onClick={() => setUpdateDialogOpen(true)}
                    >
                      Apply Update
                      <DownloadIcon />
                    </CanvasButton>
                  </div>
                </SimpleTooltip>

                <SimpleTooltip message="Update your feed's logic automatically when the author of the custom node updates the node's logic. Your parameter values will be preserved.">
                  <CanvasButton
                    variant="ghost"
                    className="flex-1"
                    onClick={() =>
                      updateShape(shape, {
                        metadata: {
                          ...shape.props.metadata,
                          customNodeAutoUpdate: true,
                        },
                      })
                    }
                  >
                    Enable Auto-Update
                  </CanvasButton>
                </SimpleTooltip>
              </div>
            )}

            {isReadOnly && !shape.props.metadata?.customNodeAutoUpdate && (
              <BlockSeparator />
            )}

            {groups && (
              <>
                {Object.entries(groups).map(
                  ([groupId, groupParameters], groupIndex) => {
                    const group =
                      shape.props.filter.metadata?.customNodeParameterGroups?.find(
                        (group) => group.id === groupId,
                      );

                    return (
                      <Fragment key={groupId}>
                        <div
                          className={cn(
                            "flex flex-col gap-2 px-2",
                            groupIndex === 0 && "pt-2",
                          )}
                        >
                          {group && (
                            <div className="flex flex-col gap-2">
                              {group.name && (
                                <div className="text-lg font-bold">
                                  {group.name}
                                </div>
                              )}
                              {group.description && (
                                <div className="text-sm text-slate-dim">
                                  {group.description}
                                </div>
                              )}
                            </div>
                          )}
                          <div
                            className="grid items-start gap-2"
                            style={{ gridTemplateColumns: "auto 1fr" }}
                          >
                            {groupParameters?.map((parameter) => {
                              if (isReadOnly) {
                                return (
                                  <div
                                    key={parameter.name}
                                    className="contents"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Label
                                        htmlFor={parameter.name}
                                        className="py-2"
                                      >
                                        {parameter.displayName ||
                                          parameter.name}
                                      </Label>
                                      {parameter.description && (
                                        <SimpleTooltip
                                          message={parameter.description}
                                        >
                                          <InfoCircledIcon className="opacity-30" />
                                        </SimpleTooltip>
                                      )}
                                    </div>
                                    {parameter.type === "input" ||
                                    parameter.type === "regex" ||
                                    !parameter.type ? (
                                      <ReadOnlyValue
                                        id={parameter.name}
                                        className="w-fit"
                                      >
                                        {parameter.value ??
                                          parameter.exampleValue}
                                      </ReadOnlyValue>
                                    ) : parameter.type === "number" ? (
                                      <ReadOnlyValue>
                                        {parameter.isPercentage
                                          ? `${
                                              (parameter.value ??
                                                parameter.exampleValue) * 100
                                            }%`
                                          : parameter.value ??
                                            parameter.exampleValue}
                                      </ReadOnlyValue>
                                    ) : parameter.type === "select" ? (
                                      <ReadOnlyValue>
                                        {parameter.labels
                                          ? parameter.labels[
                                              parameter.options.findIndex(
                                                (option) =>
                                                  option ===
                                                  (parameter.value ??
                                                    parameter.exampleValue),
                                              )
                                            ]
                                          : parameter.value ??
                                            parameter.exampleValue}
                                      </ReadOnlyValue>
                                    ) : parameter.type === "toggle" ? (
                                      <Switch
                                        checked={
                                          parameter.value ??
                                          parameter.exampleValue
                                        }
                                      />
                                    ) : (
                                      parameter.type === "list" && (
                                        <div className="flex flex-wrap gap-1.5">
                                          {(
                                            parameter.value ??
                                            parameter.exampleValue
                                          ).map((value, index) => (
                                            <ReadOnlyValue
                                              // eslint-disable-next-line @eslint-react/no-array-index-key
                                              key={`param-value-${shape.id}-${index}`}
                                              id={parameter.name}
                                            >
                                              {value}
                                            </ReadOnlyValue>
                                          ))}
                                        </div>
                                      )
                                    )}
                                  </div>
                                );
                              }

                              return (
                                <div key={parameter.name} className="contents">
                                  <div className="flex items-center gap-2">
                                    <Label
                                      htmlFor={parameter.name}
                                      className="py-3.5"
                                    >
                                      {parameter.displayName || parameter.name}
                                    </Label>
                                    {parameter.description && (
                                      <SimpleTooltip
                                        message={parameter.description}
                                      >
                                        <InfoCircledIcon className="opacity-30" />
                                      </SimpleTooltip>
                                    )}
                                  </div>
                                  {parameter.type === "input" ||
                                  !parameter.type ? (
                                    <CanvasInput
                                      id={parameter.name}
                                      value={
                                        parameter.value ??
                                        parameter.exampleValue
                                      }
                                      onValueChange={(value) => {
                                        updateShape(shape, {
                                          ...shape.props,
                                          filter: {
                                            ...shape.props.filter,
                                            metadata: {
                                              ...shape.props.filter.metadata,
                                              customNodeParameters:
                                                shape.props.filter.metadata?.customNodeParameters?.map(
                                                  (param, index) =>
                                                    index ===
                                                    parameter.originalIndex
                                                      ? ({
                                                          ...param,
                                                          value: value,
                                                        } as FeedInputParameter)
                                                      : param,
                                                ),
                                            },
                                          },
                                        });
                                      }}
                                    />
                                  ) : parameter.type === "regex" ? (
                                    <CanvasRegexTextArea
                                      isReadOnly={false}
                                      value={
                                        parameter.value ??
                                        parameter.exampleValue
                                      }
                                      onChange={(v) => {
                                        updateShape(shape, {
                                          ...shape.props,
                                          filter: {
                                            ...shape.props.filter,
                                            metadata: {
                                              ...shape.props.filter.metadata,
                                              customNodeParameters:
                                                shape.props.filter.metadata?.customNodeParameters?.map(
                                                  (param, index) =>
                                                    index ===
                                                    parameter.originalIndex
                                                      ? ({
                                                          ...param,
                                                          value: v,
                                                        } as FeedInputParameter)
                                                      : param,
                                                ),
                                            },
                                          },
                                        });
                                      }}
                                    />
                                  ) : parameter.type === "number" ? (
                                    parameter.isPercentage ? (
                                      <CanvasPercentageInput
                                        value={
                                          parameter.value ??
                                          parameter.exampleValue
                                        }
                                        onValueChange={(v) => {
                                          updateShape(shape, {
                                            ...shape.props,
                                            filter: {
                                              ...shape.props.filter,
                                              metadata: {
                                                ...shape.props.filter.metadata,
                                                customNodeParameters:
                                                  shape.props.filter.metadata?.customNodeParameters?.map(
                                                    (param, index) =>
                                                      index ===
                                                      parameter.originalIndex
                                                        ? ({
                                                            ...param,
                                                            value: v || 0,
                                                          } as FeedNumberParameter)
                                                        : param,
                                                  ),
                                              },
                                            },
                                          });
                                        }}
                                      />
                                    ) : (
                                      <CanvasNumberInput
                                        value={
                                          parameter.value ??
                                          parameter.exampleValue
                                        }
                                        onValueChange={(v) => {
                                          updateShape(shape, {
                                            ...shape.props,
                                            filter: {
                                              ...shape.props.filter,
                                              metadata: {
                                                ...shape.props.filter.metadata,
                                                customNodeParameters:
                                                  shape.props.filter.metadata?.customNodeParameters?.map(
                                                    (param, index) =>
                                                      index ===
                                                      parameter.originalIndex
                                                        ? ({
                                                            ...param,
                                                            value: v || 0,
                                                          } as FeedNumberParameter)
                                                        : param,
                                                  ),
                                              },
                                            },
                                          });
                                        }}
                                      />
                                    )
                                  ) : parameter.type === "toggle" ? (
                                    <div className="flex items-center justify-end h-full">
                                      <CanvasSwitch
                                        checked={
                                          parameter.value ??
                                          parameter.exampleValue
                                        }
                                        onCheckedChange={(v) => {
                                          updateShape(shape, {
                                            ...shape.props,
                                            filter: {
                                              ...shape.props.filter,
                                              metadata: {
                                                ...shape.props.filter.metadata,
                                                customNodeParameters:
                                                  shape.props.filter.metadata?.customNodeParameters?.map(
                                                    (param, index) =>
                                                      index ===
                                                      parameter.originalIndex
                                                        ? ({
                                                            ...param,
                                                            value: v,
                                                          } as FeedToggleParameter)
                                                        : param,
                                                  ),
                                              },
                                            },
                                          });
                                        }}
                                      />
                                    </div>
                                  ) : parameter.type === "list" ? (
                                    <ListParamater
                                      parameter={parameter}
                                      shape={shape}
                                      parameterIndex={parameter.originalIndex}
                                      updateShape={updateShape}
                                    />
                                  ) : parameter.type === "select" ? (
                                    <CanvasSimpleSelect
                                      aria-label={parameter.name}
                                      value={
                                        parameter.value ??
                                        parameter.exampleValue
                                      }
                                      options={
                                        parameter.labels
                                          ? parameter.labels.map(
                                              (label, i) => ({
                                                value:
                                                  parameter.options[i] || "",
                                                label,
                                              }),
                                            )
                                          : parameter.options
                                      }
                                      onValueChange={(v) => {
                                        updateShape(shape, {
                                          ...shape.props,
                                          filter: {
                                            ...shape.props.filter,
                                            metadata: {
                                              ...shape.props.filter.metadata,
                                              customNodeParameters:
                                                shape.props.filter.metadata?.customNodeParameters?.map(
                                                  (param, index) =>
                                                    index ===
                                                    parameter.originalIndex
                                                      ? ({
                                                          ...param,
                                                          value: v,
                                                        } as FeedSelectParameter)
                                                      : param,
                                                ),
                                            },
                                          },
                                        });
                                      }}
                                    />
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {groupIndex !== Object.entries(groups).length - 1 && (
                          <BlockSeparator className="my-2" />
                        )}
                      </Fragment>
                    );
                  },
                )}

                <BlockSeparator />
              </>
            )}

            {shape.props.showLogic ? (
              <>
                <CanvasButton
                  variant="ghost"
                  className="flex-1"
                  alwaysEnabled
                  onClick={() => {
                    // eslint-disable-next-line @eslint-react/dom/no-flush-sync
                    flushSync(() => {
                      updateShape(shape, {
                        showLogic: false,
                        padding: {
                          ...shape.props.padding,
                          top: 82,
                        },
                      });
                    });

                    deletePreview();
                    // Height will be updated by onHeightChange after content renders
                  }}
                >
                  <span className="sr-only">Hide Custom Node</span>
                  <ChevronUpIcon />
                </CanvasButton>
                <BlockSeparator />
                {!isReadOnly && (
                  <>
                    <div className="flex items-center gap-2">
                      <SimpleTooltip
                        message="Reset parameters to defaults."
                        asChild
                      >
                        <div>
                          <CanvasButton
                            variant="ghost"
                            onClick={resetParameters}
                          >
                            <RefreshCwIcon />
                            Reset Parameters
                          </CanvasButton>
                        </div>
                      </SimpleTooltip>

                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <CanvasButton variant="ghost" className="flex-1">
                            Detach Custom Node
                          </CanvasButton>
                        </AlertDialogTrigger>
                        <AlertDialogContent {...preventBubble}>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will detach the custom node. You will no
                              longer recieve updates from the custom node. This
                              process cannot be reversed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="text-sm text-slate-normal">
                            The benefit of detaching the custom node is that you
                            can change the logic of the custom node.
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => {
                                const [parentBinding] =
                                  editor.getBindingsToShape<LayoutBinding>(
                                    shape.id,
                                    "layout",
                                  );

                                if (!parentBinding) return;

                                const parentShape = editor.getShape(
                                  parentBinding.fromId,
                                );

                                if (!parentShape) return;

                                editor.deleteShape(shape.id);
                                buildBindings({
                                  editor,
                                  toId: parentShape.id,
                                  filter: replaceCustomNodeParameters(
                                    shape.props.filter,
                                    shape.props.filter.metadata
                                      ?.customNodeParameters || [],
                                  ),
                                  index: parentBinding.props.index,
                                });
                              }}
                            >
                              Detach
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <BlockSeparator />
                  </>
                )}
              </>
            ) : (
              <SimpleTooltip
                message={
                  previewUnavailable
                    ? "This node's logic can't be shown on the canvas: it uses something the editor doesn't know how to draw."
                    : "View Custom Node"
                }
              >
                <CanvasButton
                  variant="ghost"
                  className="flex-1"
                  alwaysEnabled
                  disabled={isExample || previewUnavailable}
                  onClick={() => updateShape(shape, { showLogic: true })}
                >
                  <span className="sr-only">
                    {previewUnavailable
                      ? "Custom node logic cannot be shown"
                      : "View Custom Node"}
                  </span>
                  <ChevronDownIcon />
                </CanvasButton>
              </SimpleTooltip>
            )}
          </div>
        </BlockBody>
      </BlockContainer>

      {updateDialogOpen && latestNode && (
        <UpdateDialog
          shape={shape}
          latestNode={latestNode}
          setUpdateDialogOpen={setUpdateDialogOpen}
        />
      )}
    </BlockContainerShapeContext>
  );
}
